import { PROGRAM_FLOW_STEPS, useProgramFlowStepAction } from "../useProgramFlowStepAction";
import { useMutation, useQuery } from "@apollo/client/react";
import { useApolloCache } from "@portal/app/_components/apolloUtils";
import { object, string } from "yup";
import { Formik } from "formik";
import Typography from "@mui/material/Typography";
import { ValidatedFormikField } from "@portal/app/_components/ui-elements/formHelpers";
import { gql } from "graphql-tag";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MeForPhoneVerificationStepDocument,
  RequestCodeViaPhoneForVerificationDocument,
  VerifyPhoneCodeDocument,
} from "./generated";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import {
  ClientErrorMessage,
  detectKnownClientError,
} from "@portal/app/_common/lib/clientErrorUtils";
import { useAuthenticatedContext } from "@portal/app/_components/context/AuthenticatedContext";
import { DreamMuiHandledError } from "@portal/app/_components/ui-elements/Mui/DreamMuiHandledError";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import { MixpanelEventName } from "@portal/app/_common/types/mixpanel";
import { useFlowContext } from "@portal/app/_components/flow/FlowContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import Box from "@mui/material/Box";
import {
  ButtonSkeleton,
  TypographySkeleton,
} from "@portal/app/_components/ui-elements/Mui/DreamMuiSkeleton";
import Skeleton from "@mui/material/Skeleton";
import { faMessages } from "@fortawesome/pro-solid-svg-icons/faMessages";
import { faPhone } from "@fortawesome/pro-solid-svg-icons/faPhone";
import { StepProps } from "@portal/app/_components/flow/props";
import { DreamError } from "@portal/app/_common/lib/errorHelpers";
import { ClientFacingErrors } from "@portal/app/_common/types/clientErrors";
import { logger } from "@portal/app/_common/lib/logger";
import { dreamThrottle } from "@portal/app/_common/lib/dreamThrottle";
import { captureException } from "@sentry/nextjs";
import {
  FlowForm,
  FlowFormSubmit,
  FlowFormSubmitWrapper,
} from "@portal/app/_components/flow/steps/components/FlowForm";
import { getNextCta, getSubmitCta } from "@portal/app/_common/lib/programHelpers";

void gql`
  query meForPhoneVerificationStep {
    me {
      id
      phone
      phoneVerified
    }
  }
  mutation requestCodeViaPhoneForVerification($phone: String!, $isMobile: Boolean = true) {
    requestCodeViaPhone(phone: $phone, isMobile: $isMobile)
  }
  mutation verifyPhoneCode(
    $code: String!
    $phone: String!
    $programFlow: Boolean!
    $programId: ID
    $questionnaireFlow: Boolean!
    $skipperFlow: Boolean!
    $flowIntent: FlowIntent
    $signInFlow: Boolean!
  ) {
    verifyPhoneCode(code: $code, phone: $phone) {
      id
      phone
      phoneVerified
      tokens {
        accessToken
        refreshToken
      }
      customer {
        ...FlowStateUpdateFragment
      }
    }
  }
`;

export const verificationCodeLength = 4;
export const PhoneVerificationStep = ({
  mixpanelEventMap,
  onNext,
}: StepProps<{ codeSent: MixpanelEventName; codeVerified: MixpanelEventName }> & {
  onNext?: () => void;
}) => {
  const analytics = useMixpanel();
  const beginFlowStep = useProgramFlowStepAction(PROGRAM_FLOW_STEPS.phone);
  const verifiedProgress = useRef(false);
  const { evictRootQueries } = useApolloCache();
  const [textCodeSent, setTextCodeSent] = useState(false);
  const [smsSupported, setSmsSupported] = useState(true);
  const [delayResendText, setDelayResendText] = useState(false);
  const [delayResendCall, setDelayResendCall] = useState(false);
  const [textOrCall, setTextOrCall] = useState<"TEXT" | "CALL">("TEXT");
  const { goForward, getFlowArgs } = useFlowContext();
  const { loggedIn } = useAuthenticatedContext();
  const [codeAttempted, setCodeAttempted] = useState(false);

  const { data, refetch: refetchUser } = useQuery(MeForPhoneVerificationStepDocument, {
    skip: !loggedIn,
  });
  const { phone, phoneVerified: isVerified } = data?.me ?? {};
  const [requestVerificationCodeMutation, { loading: requestLoading }] = useMutation(
    RequestCodeViaPhoneForVerificationDocument
  );
  const [verifyPhoneCodeMutation, { loading: verifyLoading }] = useMutation(
    VerifyPhoneCodeDocument,
    {
      onCompleted: () => {
        evictRootQueries([
          "resumableSchoolEnrollments",
          "currentEnrollmentItemsOfType",
          "getProgramEnrollmentItem",
        ]);
      },
    }
  );
  const [error, setError] = useState<ClientErrorMessage | null>(null);
  const loading = requestLoading || verifyLoading;

  const requestVerificationCode = useMemo(
    () =>
      async (phone: string, isMobile = true): Promise<boolean | DreamError> => {
        try {
          const { data } = await requestVerificationCodeMutation({
            variables: {
              phone,
              isMobile,
            },
          });
          return !!data?.requestCodeViaPhone;
        } catch (e) {
          const clientError = detectKnownClientError(e);
          if (isMobile && clientError === ClientFacingErrors.SmsNotSupported) {
            return new DreamError(ClientFacingErrors.SmsNotSupported);
          } else if (clientError === ClientFacingErrors.InvalidPhoneNumber) {
            setError({
              title: "Invalid phone number",
              description:
                "This phone number is invalid. Please go back and enter a phone number that can receive a text or call.",
            });
          } else if (
            clientError === ClientFacingErrors.RateLimit ||
            clientError === ClientFacingErrors.MaxSendAttempts
          ) {
            setError({
              title: "You tried too many times",
              description: "You will need to wait a little before trying another code.",
            });
          } else {
            captureException(e);
            logger.error(e);
            setError({
              title: "We couldn't text that number",
              description: "Please try again in a minute.",
            });
          }
        }
        return false;
      },
    [requestVerificationCodeMutation]
  );

  const verifyPhoneCode = useMemo(
    () =>
      dreamThrottle(
        async ({
          phone,
          code,
          onVerified,
        }: {
          phone: string;
          code: string;
          onVerified: () => void;
        }): Promise<boolean> => {
          try {
            setError(null);
            const { data } = await verifyPhoneCodeMutation({
              update: (_cache, { data }) => {
                if (data?.verifyPhoneCode?.phoneVerified === true) onVerified();
              },
              variables: {
                code,
                phone,
                ...getFlowArgs(),
              },
            });
            if (data?.verifyPhoneCode?.phoneVerified !== true) {
              setError({
                title: "We couldn't verify your phone number",
                description: "Please re-enter the code and try again.",
              });
              return false;
            }
            return true;
          } catch (e) {
            const clientError = detectKnownClientError(e);
            if (
              clientError === ClientFacingErrors.RateLimit ||
              clientError === ClientFacingErrors.MaxSendAttempts
            ) {
              setError({
                title: "You tried too many times",
                description: "You will need to wait a little before trying another code.",
              });
            } else if (clientError === ClientFacingErrors.InvalidVerifyCode) {
              setError({
                title: "Invalid code",
                description: "This code is either incorrect or expired. Please try again.",
              });
            } else {
              captureException(e);
              logger.error(e);
              setError({
                title: "That code didn't work",
                description:
                  "The code you used is wrong or expired. Please re-enter the code or start over to get a new code.",
              });
            }
            return false;
          }
        }
      ),
    [getFlowArgs, verifyPhoneCodeMutation]
  );

  const onClickSendCode = useCallback(
    async (shouldCall?: true) => {
      setCodeAttempted(true);
      setTextOrCall(shouldCall ? "CALL" : "TEXT");
      setError(null);
      if (!phone) {
        return;
      }
      await requestVerificationCode(phone, !shouldCall).then(async (successOrError) => {
        if (typeof successOrError === "boolean") {
          await analytics.track(mixpanelEventMap.codeSent, {
            method: shouldCall ? "CALL" : "TEXT",
          });
          setTextCodeSent(successOrError);
          setDelayResendText(true);
          setTimeout(() => {
            setDelayResendText(false);
          }, 15000);
        } else {
          const clientError = detectKnownClientError(successOrError);
          if (clientError === "SMS_NOT_SUPPORTED") {
            setSmsSupported(false);
          }
        }
      });
    },
    [analytics, mixpanelEventMap.codeSent, phone, requestVerificationCode]
  );

  useEffect(() => {
    if (phone && isVerified === false && !codeAttempted) {
      void onClickSendCode();
    }
  }, [phone, isVerified, codeAttempted, onClickSendCode, setCodeAttempted]);

  if (!data) {
    return (
      <>
        <TypographySkeleton lines={2} />
        <ButtonSkeleton />
        <ButtonSkeleton />
      </>
    );
  }

  if (!phone) {
    return <DreamMuiHandledError />;
  }

  if (isVerified) {
    return (
      <AlreadyVerifiedContainer>
        <Typography
          variant="new_h2"
          sx={{
            mb: 1,
            mt: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box sx={{ color: "success.main", pr: 1 }}>
            <FontAwesomeIcon icon={faCheckCircle} size="2x" />
          </Box>
          Your number has been verified!
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, justifyContent: "end" }}>
          <FlowFormSubmit
            onClick={() => {
              if (!verifiedProgress.current && beginFlowStep()?.()) {
                verifiedProgress.current = true;
              }
              goForward();
              onNext?.();
            }}
            data-testid="submit"
          >
            {getNextCta()}
          </FlowFormSubmit>
        </Box>
      </AlreadyVerifiedContainer>
    );
  }

  if (!textCodeSent && !smsSupported) {
    return (
      <>
        <Typography variant="new_h2" sx={{ mb: 1, mt: 0 }}>
          Can we call you to verify your phone number instead?
        </Typography>
        <Typography variant="subtitle1" color="#9DA4AE">
          A computer will read you a {verificationCodeLength}-digit number. Write it down and type
          it in.
        </Typography>
        <Button
          onClick={() => onClickSendCode(true)}
          disabled={loading}
          sx={{ mt: 1 }}
          data-testid="call-to-verify-button"
        >
          Call & read me the code
        </Button>
        <Error error={error} />
      </>
    );
  }

  return (
    <Formik<{ code: string }>
      initialValues={{ code: "" }}
      onSubmit={async ({ code }, { resetForm }) => {
        const completeFlowStep = beginFlowStep();
        resetForm();
        const success = await verifyPhoneCode({
          phone,
          code: code.toString(),
          onVerified: () => {
            if (!verifiedProgress.current && completeFlowStep?.()) {
              verifiedProgress.current = true;
            }
          },
        });
        if (success) {
          await analytics.track(mixpanelEventMap.codeVerified, { method: textOrCall });
          await refetchUser();
          goForward();
        }
      }}
      validateOnMount={false}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={object().shape({
        code: string()
          .matches(
            new RegExp(`^[0-9]{${verificationCodeLength}}$`, "g"),
            `The code is ${verificationCodeLength} numbers long.`
          )
          .required(
            smsSupported
              ? "Enter the code we texted you."
              : "Enter the code we gave you over the phone."
          ),
      })}
    >
      {({ values, isSubmitting, submitForm }) => {
        if (!isSubmitting && values.code.length === verificationCodeLength) {
          void submitForm();
        }
        return (
          <FlowForm noValidate data-testid="code-form">
            <Typography
              variant="new_h2"
              sx={{ mb: 1, mt: 0 }}
              data-testid="phone-verification-step-header"
            >
              {smsSupported ? (
                <>
                  <FontAwesomeIcon icon={smsSupported ? faMessages : faPhone} /> Enter{" "}
                  {verificationCodeLength}-digit number sent to
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={smsSupported ? faMessages : faPhone} /> Enter{" "}
                  {verificationCodeLength}-digit number we read you over the phone
                </>
              )}
            </Typography>
            <Typography variant="new_h2" sx={{ mb: 1, mt: 0 }}>
              +1 ••• ••• {phone.substring(6)}
            </Typography>
            {loading ? (
              <Skeleton variant="rounded" sx={{ height: "4.25rem", width: "100%" }} />
            ) : (
              <>
                <ValidatedFormikField
                  as={CodeInput}
                  name="code"
                  data-testid="code"
                  disabled={loading}
                  autoFocus
                  inputProps={{
                    placeholder: new Array(verificationCodeLength).fill("_").join(" "),
                    inputMode: "numeric",
                    autoComplete: "one-time-code",
                    autoFocus: true,
                    maxLength: verificationCodeLength,
                    "data-hj-allow": true,
                  }}
                />
                <Error error={error} />
                <FlowFormSubmitWrapper
                  sx={{
                    flexDirection: "column",
                    justifyContent: "end",
                  }}
                >
                  <Button
                    variant="text"
                    sx={{ pb: 1 }}
                    disabled={loading || delayResendCall}
                    onClick={() => {
                      setDelayResendText(true);
                      setTimeout(() => setDelayResendText(false), 15000);
                      return onClickSendCode(true);
                    }}
                  >
                    Call & read me the code instead
                  </Button>
                  <Button
                    variant="text"
                    sx={{ pb: 2 }}
                    disabled={loading || delayResendText}
                    onClick={() => {
                      setDelayResendCall(true);
                      setTimeout(() => setDelayResendCall(false), 15000);
                      return onClickSendCode();
                    }}
                  >
                    Text me again
                  </Button>
                  <FlowFormSubmit sx={{ mt: 1 }} disabled={loading}>
                    {getSubmitCta(null)} code
                  </FlowFormSubmit>
                </FlowFormSubmitWrapper>
              </>
            )}
          </FlowForm>
        );
      }}
    </Formik>
  );
};

const Error = ({ error }: { error: ClientErrorMessage | null }) =>
  error ? (
    <Alert severity={error.severity ?? "error"} data-testid="login-error" sx={{ mt: 1, mb: 1 }}>
      <AlertTitle>{error.title}</AlertTitle>
      {error.description}
    </Alert>
  ) : null;

export const CodeInput = styled(TextField)`
  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  & .MuiInputBase-input {
    padding-top: 0;
    padding-bottom: 0;
    font-size: 3rem;
  }
`;

const AlreadyVerifiedContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 1rem;
`;
